'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/common/Header';
import { PAYMENT_PRODUCTS } from '@/lib/payments/catalog';
import { formatClp } from '@/lib/payments/format';
import MercadoPagoBadge from '@/components/payments/MercadoPagoBadge';
import { startTopupCheckout } from '@/lib/payments/client-checkout';

interface PricingItem {
  id: string;
  name: string;
  type: 'free_plan' | 'subscription' | 'topup';
  price: number;
  popular?: boolean;
  tagline?: string;
  features: string[];
  footnotes?: string[];
  cta: string;
  requiresAuth?: boolean;
}

const PRICING_ITEMS: PricingItem[] = [
  {
    id: 'free',
    name: 'Plan Gratuito',
    type: 'free_plan',
    price: 0,
    tagline: 'Todo LynMet, sin pagar.',
    features: [
      '40 tokens iniciales al crear tu cuenta',
      '+10 tokens semanales para seguir avanzando',
      'Acceso completo a todas las funcionalidades',
      'Todos los mentores incluidos (Gen-IA, SocrIAtes, AI-de y AI Coach)',
      'Crea sesiones ilimitadas (según tokens disponibles)',
    ],
    footnotes: [
      'Recarga semanal los lunes solo si te quedan 5 tokens o menos.',
      'No incluye recarga mensual.',
    ],
    cta: 'Crear cuenta gratis',
    requiresAuth: false,
  },
  {
    id: 'lynmet',
    name: 'Plan LynMet',
    type: 'subscription',
    price: PAYMENT_PRODUCTS.lynmet.price,
    popular: true,
    tagline: PAYMENT_PRODUCTS.lynmet.tagline,
    features: PAYMENT_PRODUCTS.lynmet.features,
    footnotes: [
      'Recarga semanal los lunes solo si te quedan 5 tokens o menos.',
      'La recarga semanal del plan LynMet reemplaza la del plan gratuito; no se suman ambas.',
      'Pago procesado con Mercado Pago.',
    ],
    cta: 'Suscribirme',
    requiresAuth: true,
  },
];

const ORG_FEATURES = [
  'Licencias masivas para tu comunidad de emprendedores',
  'Dashboard de administrador con vista de todos los usuarios',
  'Métricas agregadas y reportes por cohorte',
  'Incorporación personalizada y capacitación del equipo',
  'SLA dedicado y soporte 24/7',
  'Tokens por usuario a medida según tu programa',
];

// Token equivalences for visual section
const TOKEN_EQUIVALENCES = [
  { tokens: 10, action: 'Diagnóstico completo AI Coach', mentor: 'AI Coach', color: '#2c78fb' },
  { tokens: 5,  action: 'Canvas B — análisis completo', mentor: 'Mentoría', color: '#3ee0be' },
  { tokens: 3,  action: 'Generación de pitches', mentor: 'Mentoría', color: '#2c78fb' },
  { tokens: 3,  action: 'Sesión de brainstorm con IA', mentor: 'AI-de', color: '#3ee0be' },
  { tokens: 4,  action: 'Plan de mejora (3 áreas clave)', mentor: 'Mentoría', color: '#2c78fb' },
  { tokens: 2,  action: 'Chat con mentor especializado', mentor: 'Gen-IA', color: '#3ee0be' },
];

interface EnterpriseFormData {
  orgName: string;
  contactName: string;
  email: string;
  orgType: string;
  estimatedUsers: string;
  message: string;
}

function EnterpriseModal({ onClose }: { onClose: () => void }) {
  const [formData, setFormData] = useState<EnterpriseFormData>({
    orgName: '', contactName: '', email: '', orgType: '', estimatedUsers: '', message: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof EnterpriseFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const orgTypes = [
    { value: 'aceleradora', label: 'Aceleradora' },
    { value: 'hub', label: 'Hub de innovación' },
    { value: 'incubadora', label: 'Incubadora' },
    { value: 'corporacion', label: 'Corporación' },
    { value: 'universidad', label: 'Universidad' },
    { value: 'otro', label: 'Otro' },
  ];

  const set = (field: keyof EnterpriseFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = (): boolean => {
    const e: Partial<Record<keyof EnterpriseFormData, string>> = {};
    if (!formData.orgName.trim()) e.orgName = 'Ingresa el nombre de tu organización';
    if (!formData.contactName.trim()) e.contactName = 'Ingresa tu nombre';
    if (!formData.email.trim()) e.email = 'Ingresa tu correo';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Correo inválido';
    if (!formData.orgType) e.orgType = 'Selecciona el tipo de organización';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating: 5,
          category: 'enterprise',
          name: formData.contactName,
          email: formData.email,
          message: `[PLAN ORGANIZACIONES]\nOrganización: ${formData.orgName}\nTipo: ${formData.orgType}\nUsuarios estimados: ${formData.estimatedUsers || 'No especificado'}\n\n${formData.message}`,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error?.message || 'No pudimos enviar tu solicitud. Inténtalo de nuevo.');
      }
      setSuccess(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Error inesperado');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center p-4"
      style={{ background: 'rgba(6,33,63,0.72)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="px-8 pt-8 pb-6" style={{ background: 'linear-gradient(135deg, #06213f 0%, #0f3f87 60%, #2c78fb 100%)' }}>
          <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors text-2xl leading-none" aria-label="Cerrar">×</button>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold mb-4"
            style={{ background: 'rgba(139,92,246,0.25)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.4)' }}>
            🏢 Para organizaciones
          </div>
          <h2 className="text-2xl font-bold text-white">Habla con nuestro equipo</h2>
          <p className="text-white/75 mt-1 text-sm">Cuéntanos sobre tu organización y te contactaremos en menos de 24 horas hábiles.</p>
        </div>
        <div className="px-8 py-6">
          {success ? (
            <div className="text-center py-8">
              <div className="text-5xl mb-4">✅</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">¡Solicitud enviada!</h3>
              <p className="text-gray-600 mb-6">Recibimos tus datos. Nuestro equipo se pondrá en contacto contigo en menos de 24 horas hábiles.</p>
              <button onClick={onClose} className="btn-primary-app px-8 py-2.5">Cerrar</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {submitError && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-600">{submitError}</div>}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Nombre de la organización <span className="text-red-500">*</span></label>
                <input type="text" value={formData.orgName} onChange={(e) => set('orgName', e.target.value)} placeholder="Ej: Aceleradora Startup Chile"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/30 focus:border-[var(--primary)] text-gray-900 text-sm" />
                {errors.orgName && <p className="text-red-500 text-xs mt-1">{errors.orgName}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Tipo de organización <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-3 gap-2">
                  {orgTypes.map((t) => (
                    <button key={t.value} type="button" onClick={() => set('orgType', t.value)}
                      className={`py-2 px-3 rounded-xl border text-xs font-medium transition-all ${formData.orgType === t.value ? 'border-[var(--primary)] bg-[var(--primary)]/8 text-[var(--primary-strong)]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
                {errors.orgType && <p className="text-red-500 text-xs mt-1">{errors.orgType}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Tu nombre <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.contactName} onChange={(e) => set('contactName', e.target.value)} placeholder="Nombre y apellido"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/30 focus:border-[var(--primary)] text-gray-900 text-sm" />
                  {errors.contactName && <p className="text-red-500 text-xs mt-1">{errors.contactName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Usuarios estimados</label>
                  <select value={formData.estimatedUsers} onChange={(e) => set('estimatedUsers', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/30 focus:border-[var(--primary)] text-gray-900 text-sm bg-white">
                    <option value="">Seleccionar</option>
                    <option value="1-20">1 – 20</option>
                    <option value="21-50">21 – 50</option>
                    <option value="51-100">51 – 100</option>
                    <option value="101-250">101 – 250</option>
                    <option value="250+">250+</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Correo electrónico <span className="text-red-500">*</span></label>
                <input type="email" value={formData.email} onChange={(e) => set('email', e.target.value)} placeholder="tu@organizacion.com"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/30 focus:border-[var(--primary)] text-gray-900 text-sm" />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">¿Cómo podemos ayudarte? <span className="text-gray-400 font-normal">(opcional)</span></label>
                <textarea value={formData.message} onChange={(e) => set('message', e.target.value)} rows={3}
                  placeholder="Cuéntanos sobre tu programa, la cantidad de emprendedores que gestionas y qué esperas de LynMet..."
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/30 focus:border-[var(--primary)] text-gray-900 text-sm resize-none" />
              </div>
              <button type="submit" disabled={isSubmitting} className="btn-primary-app w-full py-3 text-base disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? (<span className="inline-flex items-center justify-center gap-2"><span className="h-4 w-4 rounded-full border-2 border-white/60 border-t-transparent animate-spin" />Enviando...</span>) : 'Enviar solicitud'}
              </button>
              <p className="text-center text-xs text-gray-400">
                También puedes escribirnos a{' '}
                <a href="mailto:contacto@lynmet.com" className="text-[var(--primary)] hover:underline">contacto@lynmet.com</a>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PricingPageContent() {
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();

  const [error, setError] = useState('');
  const [topupError, setTopupError] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [topupLoading, setTopupLoading] = useState(false);

  const handlePurchase = async (item: PricingItem) => {
    if (item.type === 'free_plan') {
      router.push(user ? '/user-dashboard' : '/register');
      return;
    }
    if (!user) { router.push('/login'); return; }
    if (item.type === 'subscription') {
      router.push('/payment/lynmet-subscription');
    }
  };

  const handleTopup = async () => {
    if (!user) { router.push('/login'); return; }
    setTopupLoading(true);
    setTopupError('');
    try {
      await startTopupCheckout({ productId: 'topup_80', router });
    } catch (err: any) {
      setTopupError(err.message || 'No se pudo iniciar el proceso de pago');
    } finally {
      setTopupLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 bg-[#f8f9fb]">
        <div className="max-w-6xl mx-auto">
          <div className="h-64 bg-white shadow-lg rounded-2xl border border-gray-200 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      {showEnterpriseModal && <EnterpriseModal onClose={() => setShowEnterpriseModal(false)} />}

      {/* ① BACKGROUND: blanco limpio con sutil textura, mucho más contraste que el gradiente azul */}
      <main className="min-h-screen pt-24 pb-20 px-6 bg-[#f8f9fb]">
        <div className="max-w-6xl mx-auto space-y-14">

          {/* ── Hero ───────────────────────────────────────────────── */}
          <div className="text-center space-y-4 pt-4">
            <div className="section-eyebrow">Planes y tokens</div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Precios de LynMet y planes para emprendedores
            </h1>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed">
              Empieza gratis y escala cuando lo necesites. Un sistema de tokens flexible que se adapta al momento de tu proyecto.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
              <Link href="/coaches" className="meta-inline text-slate-600 hover:text-[var(--primary-strong)] transition-colors">Conoce los coaches IA</Link>
              <Link href="/about-us" className="meta-inline text-slate-600 hover:text-[var(--primary-strong)] transition-colors">Cómo funciona LynMet</Link>
              <Link href="/contact" className="meta-inline text-slate-600 hover:text-[var(--primary-strong)] transition-colors">Hablar con el equipo</Link>
            </div>
            {!user && (
              <div className="mt-6 bg-white rounded-2xl border border-gray-200 shadow-sm max-w-xl mx-auto p-4 flex flex-col sm:flex-row items-center gap-4">
                <p className="text-gray-700 text-sm text-left flex-1">
                  🎁 <strong>Nuevos usuarios:</strong> Al registrarte recibes <strong>40 tokens</strong> y recargas semanales automáticas mientras los usas.
                </p>
                <button onClick={() => router.push('/register')} className="btn-primary-app whitespace-nowrap px-5 py-2.5 text-sm flex-shrink-0">
                  Crear cuenta gratis
                </button>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* ── ② PRICING CARDS: grid mejorado ───────────────────── */}
          <div className="grid md:grid-cols-3 gap-6 items-stretch pt-5">

            {/* Free — card limpio y neutro */}
            <div className="relative bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 flex flex-col">
              <div className="p-8 flex flex-col flex-1">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Plan Gratuito</h3>
                  <p className="text-gray-500 text-sm">Todo LynMet, sin pagar.</p>
                  {/* ② PRICE HIERARCHY: $0 más pequeño, enfocado en "acceso" */}
                  <div className="mt-4 flex items-end gap-1">
                    <span className="text-5xl font-black text-gray-900">$0</span>
                  </div>
                  <p className="text-gray-400 text-xs mt-1">Acceso completo · Sin tarjeta</p>
                </div>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {PRICING_ITEMS[0].features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="mt-0.5 text-emerald-500 flex-shrink-0">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {PRICING_ITEMS[0].footnotes && (
                  <div className="space-y-1 mb-6 text-xs text-gray-400 border-t border-gray-100 pt-4">
                    {PRICING_ITEMS[0].footnotes.map((n, i) => <p key={i}>{n}</p>)}
                  </div>
                )}
                <button
                  onClick={() => handlePurchase(PRICING_ITEMS[0])}
                  className="w-full py-3 px-6 rounded-xl font-semibold text-sm border-2 border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50 transition-all"
                >
                  Crear cuenta gratis
                </button>
              </div>
            </div>

            {/* ② LynMet — DARK featured card, se diferencia radicalmente */}
            <div
              className="relative rounded-2xl shadow-2xl hover:shadow-[0_32px_64px_rgba(44,120,251,0.35)] transition-all hover:-translate-y-1 flex flex-col"
              style={{ background: 'linear-gradient(160deg, #06213f 0%, #0b3272 45%, #1a5bbf 100%)' }}
            >
              {/* Subtle glow top */}
              <div className="absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(62,224,190,0.6), transparent)' }} />

              {/* Popular badge — centered at top border */}
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap">
                <span className="text-xs font-bold px-3 py-1 rounded-full text-[#06213f]" style={{ background: '#3ee0be' }}>
                  ✦ Más popular
                </span>
              </div>

              <div className="p-8 flex flex-col flex-1">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-white mb-1">Plan LynMet</h3>
                  <p className="text-white/60 text-sm">{PRICING_ITEMS[1].tagline}</p>
                  {/* ② PRICE HIERARCHY: precio grande y dominante con /mes prominente */}
                  <div className="mt-4 flex items-end gap-2">
                    <span className="text-5xl font-black text-white">{formatClp(PRICING_ITEMS[1].price)}</span>
                    <span className="text-white/50 text-base font-medium mb-1.5">/mes</span>
                  </div>
                  <p className="text-white/40 text-xs mt-1">Suscripción mensual · Mercado Pago</p>
                </div>

                <ul className="space-y-2.5 mb-8 flex-1">
                  {PRICING_ITEMS[1].features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/85">
                      <span className="mt-0.5 flex-shrink-0" style={{ color: '#3ee0be' }}>✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {PRICING_ITEMS[1].footnotes && (
                  <div className="space-y-1 mb-6 text-xs text-white/30 border-t border-white/10 pt-4">
                    {PRICING_ITEMS[1].footnotes.map((n, i) => <p key={i}>{n}</p>)}
                  </div>
                )}

                <button
                  onClick={() => handlePurchase(PRICING_ITEMS[1])}
                  className="w-full py-3 px-6 rounded-xl font-bold text-sm text-[#06213f] transition-all hover:opacity-90 active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg, #3ee0be, #2c78fb)' }}
                >
                  Suscribirme
                </button>
              </div>
            </div>

            {/* Organizaciones — acento morado, precio referencial */}
            <div className="relative bg-white rounded-2xl border border-violet-100 shadow-sm hover:shadow-md hover:border-violet-200 transition-all hover:-translate-y-0.5 flex flex-col">
              <div className="absolute inset-x-0 top-0 h-0.5 rounded-t-2xl" style={{ background: 'linear-gradient(90deg, #7c3aed, #a855f7)' }} />

              {/* Org badge — centered at top border */}
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold text-white"
                  style={{ background: '#7c3aed' }}>
                  🏢 Para organizaciones
                </div>
              </div>

              <div className="p-8 flex flex-col flex-1">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Plan Organizaciones</h3>
                  <p className="text-gray-500 text-sm">Aceleradoras, hubs e incubadoras a escala.</p>
                  {/* ② PRICE HIERARCHY: precio de referencia para anclar expectativa */}
                  <div className="mt-4">
                    <span className="text-3xl font-black text-gray-900">A medida</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Desde $49.000 CLP/mes · según tamaño</p>
                </div>

                <ul className="space-y-2.5 mb-8 flex-1">
                  {ORG_FEATURES.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="mt-0.5 flex-shrink-0" style={{ color: '#7c3aed' }}>✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => setShowEnterpriseModal(true)}
                  className="w-full py-3 px-6 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90 active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
                >
                  Hablar con ventas
                </button>
              </div>
            </div>
          </div>

          {/* ── RECARGA — debajo de los planes ────────────────────── */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="flex flex-col md:flex-row">
              {/* Left: info block */}
              <div className="flex-1 px-8 py-7 border-b md:border-b-0 md:border-r border-gray-100">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-bold text-gray-900">⊕ Recarga de tokens</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                    style={{ background: 'rgba(44,120,251,0.08)', color: '#2c78fb', border: '1px solid rgba(44,120,251,0.16)' }}>
                    Solo Plan LynMet
                  </span>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">
                  Para cuando necesitas empujar una sesión hoy, sin esperar la recarga automática del lunes. Los tokens se acreditan al instante y <strong className="text-gray-700">no tienen fecha de vencimiento</strong>.
                </p>
                <div className="flex gap-5">
                  {['Se suma a tu saldo actual', 'Sin fecha de vencimiento', 'Mercado Pago'].map((tag) => (
                    <div key={tag} className="flex items-center gap-1.5 text-xs text-gray-400">
                      <span className="text-emerald-500">✓</span> {tag}
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: purchase block */}
              <div className="flex-shrink-0 md:w-64 px-8 py-7 flex flex-col justify-center" style={{ background: 'rgba(44,120,251,0.02)' }}>
                <div className="mb-1">
                  <span className="text-3xl font-black text-gray-900">{formatClp(PAYMENT_PRODUCTS.topup_80.price)}</span>
                  <span className="text-gray-400 text-sm ml-1">CLP</span>
                </div>
                <p className="text-xs text-gray-500 mb-5">+80 tokens · pago único</p>
                <button
                  onClick={handleTopup}
                  disabled={topupLoading}
                  className="w-full py-3 px-4 rounded-xl text-sm font-bold btn-primary-app disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {topupLoading ? (
                    <span className="inline-flex items-center justify-center gap-2">
                      <span className="h-3.5 w-3.5 rounded-full border-2 border-white/60 border-t-transparent animate-spin" />
                      Procesando...
                    </span>
                  ) : 'Recargar tokens'}
                </button>
                {topupError && (
                  <p className="mt-2 text-xs text-red-500 text-center leading-snug">{topupError}</p>
                )}
                <div className="mt-3 flex justify-center">
                  <MercadoPagoBadge caption="" />
                </div>
              </div>
            </div>
          </div>

          {/* ── ④ TOKEN EQUIVALENCES — reemplaza las 3 mini-cards genéricas ── */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-8 py-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">¿Cuánto rinde un token?</h3>
                <p className="text-gray-500 text-sm mt-0.5">Ejemplos reales de lo que puedes hacer con tus tokens</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <span className="text-xs px-3 py-1 rounded-full font-semibold" style={{ background: 'rgba(44,120,251,0.08)', color: '#2c78fb' }}>40 tokens iniciales gratis</span>
                <span className="text-xs px-3 py-1 rounded-full font-semibold" style={{ background: 'rgba(62,224,190,0.10)', color: '#0a8a6a' }}>+10 cada lunes</span>
              </div>
            </div>
            <div className="divide-y divide-gray-50">
              {TOKEN_EQUIVALENCES.map((eq, i) => (
                <div key={i} className="flex items-center gap-4 px-8 py-4 hover:bg-gray-50/60 transition-colors">
                  {/* Token pill */}
                  <div className="flex-shrink-0 w-20 text-center">
                    <span className="inline-block text-sm font-black px-3 py-1.5 rounded-xl text-white"
                      style={{ background: i % 2 === 0 ? '#2c78fb' : '#0b9e7d' }}>
                      {eq.tokens} tok
                    </span>
                  </div>
                  {/* Separator line */}
                  <div className="flex-shrink-0 w-6 h-px bg-gray-200" />
                  {/* Action */}
                  <div className="flex-1">
                    <span className="text-sm font-semibold text-gray-800">{eq.action}</span>
                  </div>
                  {/* Mentor tag */}
                  <div className="flex-shrink-0 hidden sm:block">
                    <span className="text-xs text-gray-400 font-medium bg-gray-100 px-2.5 py-1 rounded-lg">{eq.mentor}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-8 py-4 bg-gray-50/50 border-t border-gray-100">
              <p className="text-xs text-gray-400 text-center">
                Los tokens se acumulan y nunca vencen. Combínalos libremente entre todos los mentores y módulos.
              </p>
            </div>
          </div>

          {/* ── Philosophy — compacta, sin card box ───────────────── */}
          <div className="max-w-3xl mx-auto text-center py-2">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Paga según el momento de tu proyecto
            </h2>
            <p className="text-gray-500 leading-relaxed">
              LynMet está pensado para emprendedores que quieren validar una idea, ordenar un negocio o mejorar su estrategia sin contratar una consultoría desde el primer día. El sistema de tokens te permite combinar diagnóstico, exploración y mentoría según lo que necesites cada mes.
            </p>
          </div>

          {/* ── ⑤ PILLARS — franja fina sin card, separados de la Recarga ── */}
          <div className="grid md:grid-cols-3 gap-8 py-4 border-y border-gray-200">
            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-xl shadow-sm" style={{ background: 'rgba(44,120,251,0.08)' }}>🔒</div>
              <div>
                <h4 className="font-semibold text-gray-900 text-sm">Pago seguro con Mercado Pago</h4>
                <p className="text-xs text-gray-500 mt-1">Flujo completo con retornos de aprobado, pendiente y cancelado</p>
              </div>
            </div>
            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-xl shadow-sm" style={{ background: 'rgba(62,224,190,0.10)' }}>💬</div>
              <div>
                <h4 className="font-semibold text-gray-900 text-sm">Soporte y gestión desde tu cuenta</h4>
                <p className="text-xs text-gray-500 mt-1">Historial de tokens, estado del plan y cancelación desde configuración</p>
              </div>
            </div>
            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-xl shadow-sm" style={{ background: 'rgba(44,120,251,0.06)' }}>⚡</div>
              <div>
                <h4 className="font-semibold text-gray-900 text-sm">Recarga instantánea sin esperas</h4>
                <p className="text-xs text-gray-500 mt-1">Los tokens se acreditan de inmediato en tu saldo al confirmar el pago</p>
              </div>
            </div>
          </div>

          {/* ── FAQ ────────────────────────────────────────────────── */}
          <div className="space-y-3">
            <h3 className="text-xl font-bold text-gray-900 text-center mb-6">Preguntas frecuentes</h3>
            {[
              { q: '¿Cómo funcionan las recargas semanales?', a: 'Plan Gratuito: +10 tokens los lunes si te quedan 5 tokens o menos. Plan LynMet: +20 tokens los lunes si te quedan 5 tokens o menos. Adicionalmente, el Plan LynMet te recarga hasta 100 tokens una vez al mes si tu saldo baja de 100.' },
              { q: '¿Qué incluye cada plan?', a: 'Ambos incluyen todas las funciones y mentores. LynMet agrega 100 tokens al activar, recarga mensual hasta 100 tokens si tu saldo baja de 100, y +20 tokens semanales si quedas en 5 o menos.' },
              { q: '¿Qué puedes hacer con 40, 80 o 100 tokens?', a: '40 tokens: 1 diagnóstico potente o 3 sesiones de ideación. 80 tokens: 2 procesos completos o varias iteraciones entre mentores. 100 tokens: continuidad mensual con diagnóstico, exploración y planes de mejora.' },
              { q: '¿Los tokens se acumulan?', a: 'Sí, se mantienen en tu saldo indefinidamente. Las recargas semanales solo se aplican si estás bajo el umbral de 5 tokens.' },
              { q: '¿Cómo funciona el sistema de tokens en la práctica?', a: 'Ejemplos reales: un diagnóstico AI Coach cuesta 10 tokens, una acción rápida 2, un pitch 3, un análisis de competencia 3 y un plan de mejora 4. Combínalos según la profundidad que necesites.' },
              { q: '¿Cómo funciona la recarga manual?', a: 'Suma 80 tokens a tu saldo por $2.900 CLP. Disponible solo para usuarios del Plan LynMet desde el panel de usuario o la página de precios.' },
              { q: '¿Cómo se procesan los pagos?', a: 'Con Mercado Pago. Puedes activar tu plan, volver desde el checkout y gestionar tu suscripción desde configuración de cuenta.' },
              { q: '¿Puedo cancelar el plan LynMet desde la app?', a: 'Sí. Ve a Configuración → Plan y pagos → Cancelar suscripción.' },
              { q: '¿LynMet tiene planes para organizaciones?', a: 'Sí. El Plan Organizaciones incluye dashboard de administrador, reportes por cohorte, tokens a medida y soporte dedicado. Contáctanos para un precio personalizado según el tamaño de tu comunidad.' },
            ].map((faq, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                className="w-full text-left bg-white rounded-xl border border-gray-200 px-6 py-4 shadow-sm hover:shadow-md hover:border-gray-300 transition-all"
              >
                <div className="flex items-center justify-between gap-4">
                  <h4 className="font-semibold text-gray-900 text-sm">{faq.q}</h4>
                  <span className="text-gray-300 text-lg flex-shrink-0 font-light">{openFaqIndex === index ? '−' : '+'}</span>
                </div>
                {openFaqIndex === index && (
                  <p className="text-gray-500 text-sm mt-3 leading-relaxed border-t border-gray-100 pt-3">{faq.a}</p>
                )}
              </button>
            ))}
          </div>

        </div>
      </main>
    </>
  );
}
